import { dlElementConstants } from "@repo/analytics/data-layer.constants";
import type React from "react";
import { influencerPageEventNames } from "../../constants/influencer-page.constants";
import { FIREWORK_EVENTS } from "../newRelicTracking";
import { trackEnrichedClick, trackRawView } from "./analytics";
import { resolveApiVideoMeta, resolveLivestreamStatus } from "../../utils/fireworkVideoMeta";
import type { VideoDetails } from "./storyBlockTypes";
import {
  resolveVideoDescription,
  videoDescriptionMap,
  videoMetaMap,
  statMapper,
  FW_PRODUCT_CLICK_EVENTS,
} from "./storyBlockUtils";

export type HandlersContext = {
  origin: string | undefined;
  title: string;
  titleCt?: string;
  getCustomerId: () => string;
  videoId?: string;
  containerRef: React.RefObject<HTMLDivElement | null>;
  setIsLoading: (v: boolean) => void;
  fireworkNodeRef: React.MutableRefObject<HTMLElement | null>;
  initVideoTracker: (el: HTMLElement) => void;
  lastVideoIdRef: React.MutableRefObject<string | null>;
  currentVideoTitleRef: React.MutableRefObject<string>;
  currentVideoIdRef: React.MutableRefObject<string>;
  currentLiveStreamStatusRef: React.MutableRefObject<string>;
  trackFireworkEvent: (ev: string) => (e: Event) => void;
  setStoryblockScopeZIndexForPip: (enabled: boolean) => void;
};

export function createStoryblockHandlers(ctx: HandlersContext) {
  const {
    origin,
    title,
    titleCt,
    getCustomerId,
    videoId,
    containerRef,
    setIsLoading,
    fireworkNodeRef,
    initVideoTracker,
    lastVideoIdRef,
    currentVideoTitleRef,
    currentVideoIdRef,
    currentLiveStreamStatusRef,
    trackFireworkEvent,
    setStoryblockScopeZIndexForPip,
  } = ctx;

  const allFireworkEvents = Object.values(FIREWORK_EVENTS).flatMap((group) =>
    typeof group === "object" ? Object.values(group) : []
  );

  const fireworkEventHandlers: Record<string, EventListener> = {};
  for (const evName of allFireworkEvents) {
    const handler = (e: Event) => {
      try {
        const fn = trackFireworkEvent(evName);
        if (typeof fn === "function") fn(e);
      } catch {
        void 0;
      }
    };
    fireworkEventHandlers[evName] = handler;
  }

  // Add influencer-specific events (logged separately)
  const influencerEvents = Object.values(influencerPageEventNames || {});
  for (const name of influencerEvents) {
    fireworkEventHandlers[name] = () => undefined;
  }

  const handleThumbnailClick = (event: Event): void => {
    const customEvent = event as CustomEvent;
    const videoDetails = (customEvent?.detail || {}) as VideoDetails;
    statMapper(customEvent?.type);
    const vid = videoDetails?.video?.encoded_id || "";
    const description = resolveVideoDescription(videoDetails, vid);
    trackEnrichedClick({
      EVENT_NAME: dlElementConstants.LIVE_COMMERCE_PAGE_CLICKED,
      origin,
      title,
      titleCt,
      videoDetails,
      customerId: getCustomerId(),
      eventType: customEvent.type,
      eventTypeValue: "click",
      extra: description ? { EP_SEGMENT: description } : undefined,
    });
  };

  const handleContentReady = (event: Event): void => {
    const customEvent = event as CustomEvent;
    const videoDetails = (customEvent?.detail || {}) as VideoDetails;

    (window as unknown as { __fwImpressionId?: string }).__fwImpressionId =
      videoDetails?.video?.encoded_id;

    if (videoDetails?.video?.caption) {
      currentVideoTitleRef.current = videoDetails.video.caption;
    }
    const contentReadyMeta = resolveApiVideoMeta({
      eventDetail: videoDetails,
      fallbackVideoId: videoId,
      videoMetaMap,
    });
    if (!videoDetails?.video?.caption && contentReadyMeta.contentTitle) {
      currentVideoTitleRef.current = contentReadyMeta.contentTitle;
    }
    currentVideoIdRef.current =
      videoDetails?.video?.encoded_id ||
      videoDetails?.video?.id ||
      contentReadyMeta.videoId ||
      videoId ||
      "NA";
    currentLiveStreamStatusRef.current = resolveLivestreamStatus(videoDetails);

    if (lastVideoIdRef.current === currentVideoIdRef.current) {
      return;
    }
    lastVideoIdRef.current = currentVideoIdRef.current || null;
    trackRawView({
      origin,
      title,
      titleCt,
      customerId: getCustomerId(),
      currentVideoId: currentVideoIdRef.current,
      currentVideoTitle: videoDetails?.video?.caption,
      currentLiveStreamStatus: currentLiveStreamStatusRef.current,
      videoDetails,
    });
  };

  const handlePrimaryCtaClick = (event: Event): void => {
    const customEvent = event as CustomEvent;
    const { product } = (customEvent?.detail || {}) as VideoDetails;
    const productTitle = product?.product_name || "View Offers";
    const videoDetails = (customEvent?.detail || {}) as VideoDetails;
    const vid = videoDetails?.video?.encoded_id || "";
    const videoDescription = resolveVideoDescription(videoDetails, vid);

    trackEnrichedClick({
      EVENT_NAME: dlElementConstants.LIVE_COMMERCE_PAGE_CLICKED,
      EP_PRODUCT_TITLE: productTitle,
      EP_PRODUCT_ID: product?.product_id ?? "NA",
      origin,
      title,
      titleCt,
      videoDetails,
      customerId: getCustomerId(),
      eventType: customEvent.type,
      eventTypeValue: "click",
      extra: {
        EP_PRODUCT_NAME: videoDescription,
        EP_PRODUCT_CATEGORY: `${videoDescription} || ${vid}`,
        EP_SEGMENT: videoDescription,
      },
    });
  };

  const videoGenerated = (event: Event): void => {
    const customEvent = event as CustomEvent;
    const videoDetails = (customEvent?.detail || {}) as VideoDetails;
    const generatedVideoId = videoDetails?.video?.id || videoDetails?.video?.encoded_id;
    const generatedDescription =
      videoDetails?.video?.description || videoDetails?.video?.event_description;
    if (generatedVideoId && generatedDescription) {
      videoDescriptionMap[generatedVideoId] = generatedDescription;
      if (typeof window !== "undefined") {
        const w = window as unknown as { __fwVideoDescriptionMap?: Record<string, string> };
        w.__fwVideoDescriptionMap = {
          ...(w.__fwVideoDescriptionMap || {}),
          [generatedVideoId]: generatedDescription,
        };
      }
    }
    if (videoDetails?.video?.caption) {
      currentVideoTitleRef.current = videoDetails.video.caption;
    }
    const generatedMeta = resolveApiVideoMeta({
      eventDetail: videoDetails,
      fallbackVideoId: videoId,
      videoMetaMap,
    });
    if (!videoDetails?.video?.caption && generatedMeta.contentTitle) {
      currentVideoTitleRef.current = generatedMeta.contentTitle;
    }
    currentVideoIdRef.current =
      videoDetails?.video?.encoded_id ||
      videoDetails?.video?.id ||
      generatedMeta.videoId ||
      videoId ||
      "NA";
    currentLiveStreamStatusRef.current = resolveLivestreamStatus(videoDetails);
    if (fireworkNodeRef.current) {
      initVideoTracker(fireworkNodeRef.current);

      fireworkNodeRef.current.removeEventListener(
        "fw:shopping:product-clicked",
        handlePrimaryCtaClick as EventListener
      );
      fireworkNodeRef.current.addEventListener(
        "fw:shopping:product-clicked",
        handlePrimaryCtaClick as EventListener
      );
    }

    if (origin === "influencers" || origin === "live") {
      setIsLoading(false);
    } else {
      const targetElement = containerRef.current?.querySelector(".storyBlock");
      if (targetElement) {
        targetElement.classList.remove("skeleton-loading", "line-3");
        setIsLoading(false);
      }
    }

    if (fireworkNodeRef.current) {
      FW_PRODUCT_CLICK_EVENTS.forEach((eventName) => {
        fireworkNodeRef.current?.addEventListener(
          eventName,
          handlePrimaryCtaClick as EventListener
        );
      });
    }
  };

  const handlePipMode = (event: Event): void => {
    const customEvent = event as CustomEvent;
    if (customEvent?.detail?.transitionTo === "default") {
      return;
    }
    const { transitionTo } = customEvent?.detail || {};
    const videoDetails = (customEvent?.detail || {}) as VideoDetails;

    if (transitionTo === "minimized") {
      setStoryblockScopeZIndexForPip(true);
    } else if (transitionTo === "maximized" || transitionTo === "fullscreen") {
      setStoryblockScopeZIndexForPip(false);
    }

    let ctaValue = "PIP_TRANSITION";
    if (transitionTo === "minimized") {
      ctaValue = "MINIMIZE";
    } else if (transitionTo === "maximized" || transitionTo === "fullscreen") {
      ctaValue = "MAXIMIZE";
    }

    trackEnrichedClick({
      EVENT_NAME: dlElementConstants.LIVE_COMMERCE_PAGE_CLICKED,
      origin,
      title,
      titleCt,
      videoDetails,
      customerId: getCustomerId(),
      eventType: customEvent.type,
      eventTypeValue: "click",
      extra: { EP_CTA: ctaValue, EP_CTA_TEXT: ctaValue },
    });
  };

  const handlePipAction = (event: Event): void => {
    const customEvent = event as CustomEvent;

    if (customEvent?.type === "fw:player:quit" || customEvent?.type === "fw:player:navigate") {
      setStoryblockScopeZIndexForPip(false);
      return;
    }

    const videoDetails = (customEvent.detail || {}) as VideoDetails;

    if (videoDetails?.transitionTo === "minimized") {
      setStoryblockScopeZIndexForPip(true);
    } else {
      setStoryblockScopeZIndexForPip(false);
    }

    trackEnrichedClick({
      EVENT_NAME: dlElementConstants.LIVE_COMMERCE_PAGE_CLICKED,
      origin,
      title,
      titleCt,
      videoDetails,
      customerId: getCustomerId(),
      eventType: customEvent.type,
      eventTypeValue: "click",
    });
  };

  const handleVideoStart = (event: Event): void => {
    const customEvent = event as CustomEvent;
    const videoDetails = (customEvent?.detail || {}) as VideoDetails;
    if (videoDetails?.video?.caption) {
      currentVideoTitleRef.current = videoDetails.video.caption;
    }
    const startMeta = resolveApiVideoMeta({
      eventDetail: videoDetails,
      fallbackVideoId: videoId,
      videoMetaMap,
    });
    if (!videoDetails?.video?.caption && startMeta.contentTitle) {
      currentVideoTitleRef.current = startMeta.contentTitle;
    }
    currentVideoIdRef.current =
      videoDetails?.video?.encoded_id ||
      videoDetails?.video?.id ||
      startMeta.videoId ||
      videoId ||
      "NA";
    currentLiveStreamStatusRef.current = resolveLivestreamStatus(videoDetails);
    if (fireworkNodeRef.current) {
      initVideoTracker(fireworkNodeRef.current);
    }
  };

  const handleVideoComplete = (): void => {};

  return {
    fireworkEventHandlers,
    thumbnailClick: handleThumbnailClick,
    contentReady: handleContentReady,
    videoGenerated,
    productClick: handlePrimaryCtaClick,
    videoStart: handleVideoStart,
    videoComplete: handleVideoComplete,
    pipMode: (e: Event) => handlePipMode(e),
    pipAction: (e: Event) => handlePipAction(e),
  };
}
